/**
 * 中国可用性 — 集中式文案常量
 *
 * 所有页面（列表、详情、筛选、Admin）统一引用此文件，
 * 避免分散硬编码，便于合规审查与维护。
 *
 * ⚠️ 本文件只改"展示文案"，不改动业务枚举值与数据库存储。
 *    DB / API 中的值仍为 'direct' | 'vpn' | 'mirror' | 'blocked' | 'cn_native' | 'unknown'
 */

import type { AvailabilityCN } from '../types';

/* ====== 标签文案 ====== */
export const availabilityCNLabels: Record<AvailabilityCN, string> = {
  direct:    '可直连',
  vpn:       '访问受限',
  mirror:    '有镜像站',
  blocked:   '不可用',
  cn_native: '国产服务',
  unknown:   '待验证',
};

/* ====== 带前缀的完整标签（用于详情页等长文案场景） ====== */
export const availabilityCNFullLabels: Record<AvailabilityCN, string> = {
  direct:    '中国可用：可直连',
  vpn:       '中国可用：需合规跨境连接',
  mirror:    '中国可用：有镜像站',
  blocked:   '中国可用：不可用',
  cn_native: '中国可用：国产服务',
  unknown:   '中国可用：待验证',
};

/* ====== Tooltip 合规说明 ====== */
export const availabilityCNTooltips: Record<AvailabilityCN, string> = {
  direct:    '该服务在中国可直接访问，无需额外网络配置。',
  vpn:       '该服务依赖境外网络链路，可能在部分网络环境下不可访问。建议优先选择国内可用替代方案；企业场景请使用合规跨境连接与安全策略支持。',
  mirror:    '该服务提供中国区镜像站点，可直接通过镜像地址访问。',
  blocked:   '该服务在中国网络环境下通常不可达或不可正常使用。建议使用国内替代方案。',
  cn_native: '该服务为国内厂商提供，可直接在中国使用，无访问限制。',
  unknown:   '该服务在中国可用性尚未验证，欢迎反馈可用情况；建议同时查看国内替代方案。',
};

/* ====== 徽章颜色与 Emoji ====== */
export const availabilityCNBadge: Record<AvailabilityCN, { color: string; emoji: string }> = {
  cn_native: { color: '#fee2e2', emoji: '🇨🇳' },
  direct:    { color: '#dcfce7', emoji: '✅' },
  mirror:    { color: '#dbeafe', emoji: '🔗' },
  vpn:       { color: '#ffedd5', emoji: '🔒' },
  blocked:   { color: '#fee2e2', emoji: '🚫' },
  unknown:   { color: '#f3f4f6', emoji: '❓' },
};

/* ====== Ant Design Tag 色值（用于详情页 Tag 组件） ====== */
export const availabilityCNTagColor: Record<AvailabilityCN, string> = {
  cn_native: '#f5222d',
  direct:    '#52c41a',
  mirror:    '#1890ff',
  vpn:       '#fa8c16',
  blocked:   '#ff4d4f',
  unknown:   '#d9d9d9',
};

/* ====== 筛选选项（列表页 + Admin 筛选） ====== */
export const availabilityCNFilterOptions: { label: string; value: AvailabilityCN | '' }[] = [
  { label: '全部',            value: '' },
  { label: '🇨🇳 国产服务',   value: 'cn_native' },
  { label: '✅ 可直连',       value: 'direct' },
  { label: '🔗 有镜像站',     value: 'mirror' },
  { label: '🔒 访问受限',     value: 'vpn' },
  { label: '🚫 不可用',       value: 'blocked' },
];

/* ====== Admin 下拉选项（编辑表单） ====== */
export const availabilityCNAdminOptions: { label: string; value: AvailabilityCN }[] = [
  { value: 'cn_native', label: '🇨🇳 国产服务' },
  { value: 'direct',    label: '✅ 可直连' },
  { value: 'mirror',    label: '🔗 有镜像站' },
  { value: 'vpn',       label: '🔒 访问受限' },
  { value: 'blocked',   label: '🚫 不可用' },
  { value: 'unknown',   label: '❓ 待验证' },
];

/* ====== 合规免责声明 ====== */
export const AVAILABILITY_DISCLAIMER =
  '本站仅对工具在中国网络环境下的可达性做客观标注，不提供任何绕过网络限制的教程、工具或链接推荐。如需跨境访问，请遵循相关法律法规，采用企业合规跨境连接方案。';
